import React, { useState } from 'react';
import { X, PaperPlaneTilt, Copy, CheckCircle } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../../hooks/use-toast';
import axiosInstance from '../../api/api';

export const SendProposalModal = ({ onClose, estimate, formData }) => {
  const [client, setClient] = useState({ name: '', email: '', note: '' });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setSending(true);
    try {
      const r = await axiosInstance.post('/proposals', {
        estimate,
        job_type: formData.job_type,
        location: formData.location,
        description: formData.description,
        client_name: client.name || null,
        client_email: client.email || null,
        note: client.note || null,
      });
      setResult(r.data);
    } catch (e) {
      toast({ title: 'Could not create proposal', description: e.response?.data?.detail, variant: 'destructive' });
    }
    setSending(false);
  };

  const shareUrl = result ? `${window.location.origin}${result.share_path}` : '';

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="send-proposal-modal">
      <div className="bg-white dark:bg-[#0F172A] border-2 border-black shadow-[6px_6px_0px_#000] w-full max-w-md">
        <div className="p-4 border-b-2 border-black flex items-center justify-between bg-[#2B4C6F]">
          <h3 className="font-black uppercase text-white flex items-center gap-2">
            <PaperPlaneTilt size={18} weight="bold" /> Send to Client
          </h3>
          <button onClick={onClose} data-testid="send-proposal-close"><X size={20} weight="bold" className="text-white" /></button>
        </div>

        {result ? (
          <div className="p-5 space-y-3" data-testid="send-proposal-success">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-600 p-3 text-sm">
              <p className="font-bold flex items-center gap-1"><CheckCircle size={16} weight="fill" className="text-emerald-600" /> Proposal created!</p>
              {result.emailed && <p className="mt-1">Emailed to {client.email}.</p>}
            </div>
            <p className="text-xs font-bold uppercase text-neutral-500">Share link</p>
            <div className="flex gap-2">
              <code className="flex-1 bg-neutral-100 dark:bg-neutral-800 border-2 border-black px-2 py-2 text-xs break-all" data-testid="proposal-share-link">{shareUrl}</code>
              <Button size="sm" variant="outline" className="border-2 border-black rounded-none"
                onClick={() => { navigator.clipboard?.writeText(shareUrl); setCopied(true); }}
                data-testid="proposal-copy-link">
                {copied ? <CheckCircle size={16} className="text-emerald-600" /> : <Copy size={16} />}
              </Button>
            </div>
            <p className="text-xs text-neutral-500">Your client can review all three tiers and accept one — you'll get an email the moment they respond.</p>
            <Button className="w-full bg-[#2B4C6F] hover:bg-[#203A55] text-white border-2 border-black rounded-none" onClick={onClose} data-testid="send-proposal-done">Done</Button>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Turn this estimate into a branded proposal page your client can accept with one tap.
            </p>
            <Input placeholder="Client name" value={client.name} onChange={(e) => setClient(c => ({ ...c, name: e.target.value }))} data-testid="proposal-client-name" />
            <Input placeholder="Client email (optional — we'll send it for you)" type="email" value={client.email} onChange={(e) => setClient(c => ({ ...c, email: e.target.value }))} data-testid="proposal-client-email" />
            <textarea
              placeholder="Personal note (optional)"
              rows={3}
              value={client.note}
              onChange={(e) => setClient(c => ({ ...c, note: e.target.value }))}
              className="w-full border-2 border-black p-2 text-sm bg-white dark:bg-neutral-800"
              data-testid="proposal-note"
            />
            <Button onClick={submit} disabled={sending}
              className="w-full bg-[#D95800] hover:bg-[#B84A00] text-white font-black uppercase border-2 border-black rounded-none shadow-[3px_3px_0px_#000]"
              data-testid="proposal-create-btn">
              {sending ? 'Creating…' : 'Create Proposal Link'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
